import type React from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Divider } from '@mui/material';
import clsx from 'clsx';

import { IconButton } from '@components/button/Button';
import Icon from '@components/icon/Icon';
import Tooltip from '@components/tooltip/Tooltip';
import deriveDarkColor from '@helpers/color';
import useOnClickOutside from '@utils/hooks';

import theme from '../../../themes/colors';

export interface SelectableItem {
  id: string;
  name: string;
  color?: string;
}

interface ItemSelectorProps<T extends SelectableItem> {
  isOpen: boolean;
  onClose: () => void;
  onSelectItem: (item: T) => void;
  onCreateItem: (itemName: string, color?: string) => void;
  onEditItem?: (item: T) => void;
  onUpdateItem?: (newItem: T) => void | boolean | Promise<void | boolean>;
  selectedItems?: T[];
  className?: string;
  searchValue?: string;
  itemOptions?: T[];
  showColorSelector?: boolean;
  showEditButton?: boolean;
  createButtonText?: string;
  noItemsText?: string;
  EditModalComponent?: React.ComponentType<any>;
  selectedColor?: string;
  onColorChange?: (color: string) => void;
  itemType?: string;
  createButtonClassName?: string;
  itemRowClassName?: string;
  itemListClassName?: string;
  showItemDividers?: boolean;
  positionFixed?: boolean;
  getAnchorRect?: () => DOMRect | null;
  alwaysShowColorPill?: boolean;
  labelName?: string;
  nonEditableFields?: string[];
  showSelectionTooltip?: boolean;
  alwaysShowCreate?: boolean;
  disableFilter?: boolean;
  hideCreateWhenExists?: boolean;
  createIdleBgColor?: string;
  createActiveBgColor?: string;
  groupBy?: (item: T) => string;
  groupOrder?: string[];
  renderFooter?: (onClose: () => void) => React.ReactNode;
  createColorOptions?: string[];
  isItemDisabled?: (item: T) => boolean;
  // Disabled and drawn in grey instead of the item's colour; selection still shows
  isItemMuted?: (item: T) => boolean;
}

const ESTIMATED_ITEM_HEIGHT = 44;
const OVERSCAN = 5;

const useVirtualList = <T,>(items: T[], isActive: boolean) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(160);

  useLayoutEffect(() => {
    if (!isActive || !scrollRef.current) {
      setScrollTop(0);
      return;
    }
    setViewportHeight(scrollRef.current.clientHeight);
  }, [isActive]);

  const totalHeight = items.length * ESTIMATED_ITEM_HEIGHT;

  const startIdx = Math.max(
    0,
    Math.floor(scrollTop / ESTIMATED_ITEM_HEIGHT) - OVERSCAN
  );
  const endIdx = Math.min(
    items.length,
    Math.ceil((scrollTop + viewportHeight) / ESTIMATED_ITEM_HEIGHT) + OVERSCAN
  );

  const visibleItems = isActive ? items.slice(startIdx, endIdx) : [];
  const paddingTop = startIdx * ESTIMATED_ITEM_HEIGHT;
  const paddingBottom = Math.max(
    0,
    (items.length - endIdx) * ESTIMATED_ITEM_HEIGHT
  );

  const onScroll = useCallback(() => {
    if (scrollRef.current) {
      setScrollTop(scrollRef.current.scrollTop);
    }
  }, []);

  return {
    scrollRef,
    visibleItems,
    paddingTop,
    paddingBottom,
    totalHeight,
    onScroll,
    startIndex: startIdx
  };
};

const ItemSelector = <T extends SelectableItem>({
  isOpen,
  onClose,
  onSelectItem,
  onCreateItem,
  onEditItem,
  onUpdateItem,
  selectedItems = [],
  className = '',
  searchValue = '',
  itemOptions = [],
  showColorSelector = true,
  showEditButton = true,
  createButtonText = 'Create',
  noItemsText = 'No items found',
  EditModalComponent,
  selectedColor = '#F2E1E9',
  onColorChange,
  itemType = 'item',
  createButtonClassName,
  itemRowClassName,
  itemListClassName,
  showItemDividers = true,
  positionFixed = false,
  getAnchorRect,
  alwaysShowColorPill = false,
  labelName,
  nonEditableFields,
  showSelectionTooltip,
  alwaysShowCreate = false,
  disableFilter = false,
  hideCreateWhenExists = false,
  createIdleBgColor,
  createActiveBgColor,
  groupBy,
  groupOrder,
  renderFooter,
  createColorOptions,
  isItemDisabled,
  isItemMuted
}: ItemSelectorProps<T>) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [showCreatePalette, setShowCreatePalette] = useState(false);

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const filteredOptions = (() => {
    if (!searchValue) return itemOptions;
    if (!disableFilter) {
      return itemOptions?.filter((item) =>
        (item.name?.toLowerCase() || '').includes(
          searchValue?.toLowerCase() || ''
        )
      );
    }

    // Show the search matched items at the top of the list
    const lower = searchValue?.toLowerCase() || '';
    return [...itemOptions].sort((a, b) => {
      const aMatch = (a.name?.toLowerCase() || '').includes(lower);
      const bMatch = (b.name?.toLowerCase() || '').includes(lower);
      if (aMatch === bMatch) return 0;
      return aMatch ? -1 : 1;
    });
  })();

  const searchValueExists = itemOptions?.some(
    (item) =>
      (item.name?.toLowerCase() || '') === (searchValue?.toLowerCase() || '')
  );

  // Show the selected values first (just below the Create button) and keep the
  // rest of the options below them. Grouped lists keep their server order.
  const selectedIds = new Set(selectedItems.map((item) => item.id));
  const orderedOptions = groupBy
    ? filteredOptions ?? []
    : [
        ...selectedItems.filter((item) => item.id),
        ...(filteredOptions ?? []).filter((item) => !selectedIds.has(item.id))
      ];

  const groupedOptions = groupBy
    ? Object.entries(
        orderedOptions.reduce<Record<string, T[]>>((acc, item) => {
          const label = groupBy(item);
          (acc[label] ||= []).push(item);
          return acc;
        }, {})
      ).sort(
        ([a], [b]) =>
          (groupOrder?.indexOf(a) ?? 0) - (groupOrder?.indexOf(b) ?? 0)
      )
    : null;

  const virtualList = useVirtualList(orderedOptions, isOpen);

  const handleScroll = () => {
    setIsScrolling(true);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 800);

    virtualList.onScroll();
  };

  useOnClickOutside(dropdownRef, (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-edit-modal]')) {
      return;
    }
    const parentDropdown = dropdownRef.current?.closest(
      '[data-searchable-dropdown]'
    );
    if (parentDropdown && parentDropdown.contains(target)) {
      return;
    }
    if (isOpen && onClose) {
      e.stopPropagation();
      onClose();
    }
  });

  const handleItemClick = (item: T) => {
    onSelectItem(item);
  };

  const handleCreateClick = (itemName?: string, color?: string) => {
    const nameToCreate = itemName || searchValue;
    if (nameToCreate && nameToCreate.trim()) {
      onCreateItem(nameToCreate.trim(), color);
    }
    if (!renderFooter) onClose();
  };

  const handleEditClick = (item: T) => {
    if (!showEditButton || !EditModalComponent) return;
    setEditingItem(item);
    setEditModalOpen(true);
    onEditItem?.(item);
  };

  const handleSaveItem = async (updatedItem: T) => {
    if (onUpdateItem && editingItem) {
      const updated = await onUpdateItem(updatedItem);
      if (updated === false) return false;
    }

    // Update the selected color when a color is chosen
    if (updatedItem.color && onColorChange) {
      onColorChange(updatedItem.color);
    }

    setEditModalOpen(false);
    setEditingItem(null);
    return true;
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingItem(null);
  };

  const renderCreateButtonWithColorAndSearchValue = () => {
    const hasSearchValue = !!(searchValue && searchValue.trim());
    const isDuplicate = hideCreateWhenExists && searchValueExists;
    const isCreatable = hasSearchValue && !isDuplicate;
    if (!isCreatable && !alwaysShowCreate) {
      return null;
    }
    const useCustomCreateBg = !!(createIdleBgColor || createActiveBgColor);
    const createBgColor = hasSearchValue
      ? createActiveBgColor
      : createIdleBgColor;
    return (
      <>
        {isCreatable && (
          <div
            className={clsx(
              'mt-1 flex items-center rounded-xl',
              !useCustomCreateBg && 'bg-grey10',
              !useCustomCreateBg &&
                (isCreatable ? 'hover:bg-grey20' : 'opacity-60'),
              createButtonClassName
            )}
            style={
              useCustomCreateBg ? { backgroundColor: createBgColor } : undefined
            }
          >
            <button
              type="button"
              className={clsx(
                'flex h-full flex-1 items-center gap-2 px-4 text-left focus:outline-none',
                !useCustomCreateBg && 'py-1',
                isCreatable ? 'cursor-pointer' : 'cursor-default'
              )}
              onClick={() => {
                if (isCreatable) {
                  handleCreateClick(searchValue.trim());
                }
              }}
              tabIndex={0}
              aria-label={createButtonText}
              data-tooltip-id="create-button"
            >
              <div className="flex size-3.5 items-center justify-center">
                <Icon
                  name="plus"
                  className="size-2 text-black"
                  stroke="black"
                  fill="black"
                />
              </div>
              <div className="text-sm font-normal text-darkGrey">
                {createButtonText}
              </div>
              {showColorSelector || alwaysShowColorPill ? (
                <div className="flex items-center gap-2">
                  <div
                    className="!rounded-xl px-3 py-1.5 text-sm font-normal"
                    style={{
                      color: theme.darkGrey,
                      backgroundColor: selectedColor
                    }}
                  >
                    {searchValue}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="rounded-2xl px-4 py-1.5 text-sm font-normal">
                    {searchValue}
                  </div>
                </div>
              )}
            </button>

            <Tooltip
              id="create-button"
              type="text"
              tooltipText="Type to create new tag"
            />

            {isCreatable && showColorSelector && createColorOptions && (
              <button
                type="button"
                aria-label="Pick colour"
                className="mr-4 size-7 shrink-0 cursor-pointer rounded"
                style={{ backgroundColor: selectedColor }}
                onClick={() => setShowCreatePalette((v) => !v)}
              />
            )}
          </div>
        )}
        {isCreatable && showCreatePalette && createColorOptions && (
          <div className="flex items-center gap-2 px-4 py-2">
            {createColorOptions.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Select colour ${c}`}
                className={clsx(
                  'size-7 rounded',
                  selectedColor === c && 'border-2 border-cofeeGreyBlue11'
                )}
                style={{ backgroundColor: c }}
                onClick={() => onColorChange?.(c)}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  if (!isOpen) return null;

  const anchorRect = positionFixed && getAnchorRect ? getAnchorRect() : null;

  const showEditIcon = (name: string) =>
    showEditButton &&
    EditModalComponent &&
    (nonEditableFields?.length ? !nonEditableFields?.includes(name) : true);

  const renderItemRow = (item: T, actualIndex: number) => {
    const isSelected = selectedIds.has(item.id);
    const isMuted = isItemMuted?.(item) ?? false;
    const isDisabled = isMuted || (isItemDisabled?.(item) ?? false);

    return (
      <div key={item.id}>
        <button
          type="button"
          disabled={isDisabled}
          className={clsx(
            'mx-2 my-1 flex min-h-9 w-full items-center justify-between rounded-lg py-2 pl-3 pr-4 text-sm focus:outline-none',
            isSelected ? 'bg-blue4' : !isDisabled && 'hover:bg-grey10',
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
            isDisabled && !isMuted && 'opacity-60',
            itemRowClassName
          )}
          onClick={() => !isDisabled && handleItemClick(item)}
          tabIndex={0}
          role="option"
          aria-selected={isSelected}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {showColorSelector || alwaysShowColorPill ? (
              <div
                className={clsx(
                  'flex max-w-full items-center gap-2 rounded-xl px-3 py-1.5',
                  isMuted && 'bg-grey20 text-cofeeGreyBlue6'
                )}
                style={
                  isMuted
                    ? undefined
                    : {
                        backgroundColor: item.color || selectedColor,
                        color: deriveDarkColor(item.color || selectedColor)
                      }
                }
                data-tooltip-id={`tooltip-${item.id}`}
              >
                {isSelected && (
                  <Icon name="check-mark" size="12" stroke={theme.primary} />
                )}
                <div className="truncate text-sm font-normal">{item.name}</div>
              </div>
            ) : (
              <div className="truncate text-sm font-normal text-darkGrey">
                {item.name}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            {showEditIcon(item.name) && (
              <IconButton
                iconName="edit-grey"
                variant="transparent"
                className="size-6 !h-7 !p-0"
                buttonSize="small"
                iconSize="medium"
                handleClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  handleEditClick(item);
                }}
              />
            )}
          </div>
        </button>
        {showItemDividers && actualIndex + 1 !== orderedOptions.length && (
          <Divider className="border-cofeeNeutralInactive" />
        )}
        {showSelectionTooltip && isSelected && (
          <Tooltip
            id={`tooltip-${item.id}`}
            type="text"
            tooltipText="Already added"
            place="right"
          />
        )}
      </div>
    );
  };

  // Unified dropdown that handles both simple and complex cases
  return (
    <>
      <div
        ref={dropdownRef}
        className={clsx(
          positionFixed
            ? 'fixed z-50 max-h-60 rounded-lg bg-white shadow-lg'
            : 'absolute z-50 mt-1 max-h-60 w-full rounded-lg bg-white shadow-lg',
          className
        )}
        style={
          positionFixed && anchorRect
            ? (() => {
                const gap = 8;
                const spaceBelow = window.innerHeight - anchorRect.bottom - gap;
                const spaceAbove = anchorRect.top - gap;
                const openAbove = spaceBelow < 340 && spaceAbove > spaceBelow;
                return openAbove
                  ? {
                      bottom: window.innerHeight - anchorRect.top + gap,
                      left: anchorRect.left,
                      width: anchorRect.width,
                      maxHeight: spaceAbove,
                      overflowY: 'auto' as const
                    }
                  : {
                      top: anchorRect.bottom + gap,
                      left: anchorRect.left,
                      width: anchorRect.width,
                      maxHeight: spaceBelow,
                      overflowY: 'auto' as const
                    };
              })()
            : undefined
        }
      >
        <div
          className={clsx(
            'flex h-full flex-col gap-0.5 bg-theme px-2',
            positionFixed
              ? 'rounded-xl border-none shadow-lg'
              : ' rounded-md border border-cofeeNeutralInactive shadow-sm'
          )}
        >
          {/* Create button with search  and color fields */}
          {renderCreateButtonWithColorAndSearchValue()}

          {/* Dropdown items (selected values shown first) */}
          <div
            ref={virtualList.scrollRef}
            onScroll={handleScroll}
            className={clsx(
              'custom-scrollbar flex max-h-40 w-full flex-col overflow-y-auto',
              isScrolling && 'scrollbar-visible',
              itemListClassName
            )}
          >
            {groupedOptions ? (
              groupedOptions.map(([label, items]) => (
                <div key={label}>
                  <div className="px-4 pb-1 pt-3 text-xs text-grey">
                    {label}
                  </div>
                  {items.map((item, i) => renderItemRow(item, i))}
                </div>
              ))
            ) : (
              <>
                {virtualList.paddingTop > 0 && (
                  <div
                    style={{ minHeight: virtualList.paddingTop, flexShrink: 0 }}
                  />
                )}
                {virtualList.visibleItems.map((item, index) =>
                  renderItemRow(item, virtualList.startIndex + index)
                )}
                {virtualList.paddingBottom > 0 && (
                  <div
                    style={{
                      minHeight: virtualList.paddingBottom,
                      flexShrink: 0
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* No items found text */}
          {searchValue && orderedOptions.length === 0 && !searchValueExists && (
            <div className="p-4 text-sm text-cofeeNeutralInactive">
              {noItemsText} matching &quot;{searchValue}&quot;
            </div>
          )}

          {itemOptions.length === 0 && !searchValue && (
            <div className="px-4 py-6 text-sm text-cofeeNeutralInactive">
              {noItemsText}. Type to create a new {itemType}.
            </div>
          )}
        </div>

        {renderFooter && (
          <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t border-grey30 bg-theme px-4 py-3">
            {renderFooter(onClose)}
          </div>
        )}
      </div>

      {EditModalComponent && (
        <EditModalComponent
          isOpen={editModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveItem}
          editingItem={editingItem}
          showColorSelector={showColorSelector}
          labelName={labelName}
          anchorRect={dropdownRef.current?.getBoundingClientRect() ?? null}
        />
      )}
    </>
  );
};

export default ItemSelector;
